/**
 * Location endpoints (multi-location). An org has multiple lab locations; the user
 * switches between them. A default location always exists.
 *
 *   GET    /api/locations          → Location[]
 *   POST   /api/locations          → Location
 *   PATCH  /api/locations/{id}     → Location
 *   DELETE /api/locations/{id}
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  CreateLocationPayload,
  Location,
  UpdateLocationPayload,
} from "./types";

export function useLocations() {
  return useQuery({
    queryKey: ["locations"],
    queryFn: async (): Promise<Location[]> => {
      const { data } = await api.get<Location[]>("/locations");
      return data;
    },
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLocationPayload): Promise<Location> => {
      const { data } = await api.post<Location>("/locations", payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      payload: UpdateLocationPayload;
    }): Promise<Location> => {
      const { data } = await api.patch<Location>(`/locations/${args.id}`, args.payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/locations/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}
